import { useEffect, useRef, useState } from 'react'
import {
  TransformWrapper,
  TransformComponent,
  useTransformEffect,
  type ReactZoomPanPinchRef,
} from 'react-zoom-pan-pinch'
import { SeaSurface } from '@/components/game/SeaSurface'
import { useMyMissions, usePvpRaids, useRecits, type MapIslandDto, type MissionDto, type useWorldMap } from '@/api/hooks'
import { isReturningMission } from '@/lib/inProgress'
import { dangerTier } from '@/lib/danger'
import { expeditionTierMeta } from '@/lib/expedition'
import { MapControls } from './MapControls'
import { useTraceVisibility } from './useTraceVisibility'
import { FullscreenContainer } from './FullscreenContainer'
import { islandMarkerTransform, shouldShowName } from './geometry'
// Rendus depuis Map.tsx pour éviter de déplacer les helpers DTO/markers :
import { MapRoutes, IncomingRaidLayer, IncomingPvpRaidLayer, IslandFactionBadge, islandMarkerMeta, islandLegendKey, attackColor } from '@/pages/Map'
import type { MapTypeFilter } from './useMapTypeFilter'
import { KrakenMarker } from '@/components/game/kraken/KrakenLayer'
import { QuarryMarker } from '@/components/game/chantier/QuarryLayer'

type WorldData = NonNullable<ReturnType<typeof useWorldMap>['data']>

const HINT_KEY = 'corsalia.mapHintSeen'

/**
 * Viewport carte : RZPP (pan/zoom/pinch) au-dessus d'un `SeaSurface` FIXE. Les
 * routes (SVG) et les îles (boutons DOM) vivent DANS le transform et sont
 * contre-scalées `1/k`. Contrôles + plein écran en overlay hors transform.
 */
export function MapViewport({
  data,
  selected,
  onSelect,
  hoveredKey,
  setHoveredKey,
  onSelectFleet,
  filter,
  focusIslandId,
  focusNonce,
  huntSelected,
  onSelectHunt,
}: {
  data: WorldData
  selected: MapIslandDto | null
  onSelect: (i: MapIslandDto | null) => void
  hoveredKey: string | null
  setHoveredKey: (k: string | null) => void
  onSelectFleet?: (key: string) => void
  filter?: MapTypeFilter
  focusIslandId?: number | null
  focusNonce?: number
  /** La bête est-elle le sujet courant du panneau ? (halo accentué sur son marqueur) */
  huntSelected?: boolean
  /** Clic sur la bête : la carte demande l'ouverture du panneau de chasse. */
  onSelectHunt?: () => void
}) {
  const rzpp = useRef<ReactZoomPanPinchRef | null>(null)
  const [fullscreen, setFullscreen] = useState(false)
  const traces = useTraceVisibility()
  const [showHint, setShowHint] = useState(
    () => typeof localStorage !== 'undefined' && !localStorage.getItem(HINT_KEY),
  )
  const myIsland = data.islands.find((i) => i.isMine) ?? null

  const dismissHint = () => {
    if (!showHint) return
    setShowHint(false)
    try {
      localStorage.setItem(HINT_KEY, '1')
    } catch {
      /* ignore */
    }
  }

  const recenter = () => {
    const el = myIsland ? document.getElementById(`map-island-${myIsland.id}`) : null
    if (el) rzpp.current?.zoomToElement(el, 2, 350, 'easeOut')
    else rzpp.current?.resetTransform(300, 'easeOut')
  }

  useEffect(() => {
    if (focusIslandId == null) return
    const raf = requestAnimationFrame(() => {
      const el = document.getElementById(`map-island-${focusIslandId}`)
      if (el) rzpp.current?.zoomToElement(el, 2, 350, 'easeOut')
    })
    return () => cancelAnimationFrame(raf)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusNonce])

  const controls = (
    <MapControls
      onZoomIn={() => rzpp.current?.zoomIn(0.4, 250, 'easeOut')}
      onZoomOut={() => rzpp.current?.zoomOut(0.4, 250, 'easeOut')}
      onRecenter={recenter}
      onToggleFullscreen={() => setFullscreen((v) => !v)}
      isFullscreen={fullscreen}
      showHint={showHint}
      showTraces={traces.visible}
      onToggleTraces={traces.toggle}
    />
  )

  const board = (
    <div
      data-testid="map-board"
      className={
        fullscreen
          ? 'relative overflow-hidden rounded-2xl border border-sand-500/20 shadow-lg'
          : 'relative rounded-2xl border border-sand-500/20 shadow-lg overflow-hidden'
      }
      // Fullscreen: a centred 1:1 square (largest that fits the viewport) so the
      // square world never stretches to the screen's aspect ratio — otherwise the
      // SVG fleet/route layers (preserveAspectRatio="none") elongate the hulls.
      style={fullscreen ? { width: '100vmin', height: '100vmin' } : { aspectRatio: '1 / 1' }}
    >
      {/* Fond mer FIXE (ne pan/zoom pas). */}
      <SeaSurface />

      <TransformWrapper
        ref={rzpp}
        initialScale={1}
        minScale={1}
        maxScale={4}
        centerOnInit
        limitToBounds
        doubleClick={{ mode: 'zoomIn', step: 0.7 }}
        wheel={{ step: 0.2 }}
        panning={{ velocityDisabled: true }}
        onPanningStart={dismissHint}
        onZoomStart={dismissHint}
        onPinchStart={dismissHint}
      >
        <TransformComponent
          wrapperStyle={{ width: '100%', height: '100%' }}
          contentStyle={{ width: '100%', height: '100%' }}
        >
          <MapScene
            data={data}
            myIsland={myIsland}
            selected={selected}
            onSelect={onSelect}
            hoveredKey={hoveredKey}
            setHoveredKey={setHoveredKey}
            onSelectFleet={onSelectFleet}
            filter={filter}
            tracesVisible={traces.visible}
            huntSelected={huntSelected}
            onSelectHunt={onSelectHunt}
          />
        </TransformComponent>
      </TransformWrapper>
    </div>
  )

  // Les contrôles vivent HORS du plateau (cf. MapControls) : en mode normal, une
  // rangée juste au-dessus du cadre ; en plein écran, dans la bande laissée libre
  // par le carré 100vmin, en face du bouton de sortie.
  return (
    <FullscreenContainer active={fullscreen} onExit={() => setFullscreen(false)}>
      {fullscreen ? (
        <>
          <div className="absolute top-3 right-3 z-30">{controls}</div>
          {board}
        </>
      ) : (
        <div className="flex flex-col gap-2">
          {controls}
          {board}
        </div>
      )}
    </FullscreenContainer>
  )
}

/** Throttle `state.scale` en une valeur React via rAF (lissé, ε pour éviter le bruit). */
function useTransformScale(): number {
  const [k, setK] = useState(1)
  const raf = useRef<number | null>(null)
  const pending = useRef(1)
  useTransformEffect(({ state }) => {
    pending.current = state.scale
    if (raf.current !== null) return
    raf.current = window.requestAnimationFrame(() => {
      raf.current = null
      setK((prev) => (Math.abs(prev - pending.current) > 0.01 ? pending.current : prev))
    })
    return () => {
      if (raf.current !== null) {
        window.cancelAnimationFrame(raf.current)
        raf.current = null
      }
    }
  })
  return k
}

/**
 * Contenu DANS le transform : overlays routes + attaque entrante + boutons-îles.
 * Lit `k` via RZPP pour contre-scaler marqueurs et largeurs de trait.
 */
export function MapScene({
  data,
  myIsland,
  selected,
  onSelect,
  hoveredKey,
  setHoveredKey,
  onSelectFleet,
  filter,
  tracesVisible = true,
  huntSelected = false,
  onSelectHunt,
}: {
  data: WorldData
  myIsland: MapIslandDto | null
  selected: MapIslandDto | null
  onSelect: (i: MapIslandDto | null) => void
  hoveredKey: string | null
  setHoveredKey: (k: string | null) => void
  onSelectFleet?: (key: string) => void
  filter?: MapTypeFilter
  /** Affiche les tracés (lignes SVG) des flottes en vol. Défaut : visible. */
  tracesVisible?: boolean
  /** La bête est-elle le sujet courant du panneau ? (halo accentué) */
  huntSelected?: boolean
  /** Clic sur la bête : la carte ne sait pas ouvrir un panneau, elle le demande. */
  onSelectHunt?: () => void
}) {
  const k = useTransformScale()
  const { data: missionsData } = useMyMissions()
  const { data: pvpRaids } = usePvpRaids()
  const { data: recitsData } = useRecits()
  // ⚠️ Ce filtre jetait TOUTE mission sans île de destination, donc les deux types
  // dont la cible est un point de mer : la chasse légendaire et le convoi de
  // carrière. La branche sans île de MapRoutes était par conséquent du code mort
  // sur la carte live, et la flotte partait sans qu'aucun trait ne la suive. On
  // accepte désormais une cible portée par le payload de mission.
  const hasSeaTarget = (m: MissionDto) => typeof m.payload?.targetX === 'number' && typeof m.payload?.targetY === 'number'
  const inProgress =
    missionsData?.missions.filter(
      (m) => (m.status === 'in_progress' || isReturningMission(m)) && (m.destinationIslandId || hasSeaTarget(m)),
    ) ?? []
  // Récits in flight (sailing out OR home) get a route too; readable/done = parked.
  const recitsEnRoute =
    recitsData?.recits.filter(
      (r) => (r.state === 'traveling' || r.state === 'returning') && r.destinationIslandId,
    ) ?? []
  const incomingPvp = data.incomingPlayerRaids ?? []
  // The island pulses under attack from EITHER an NPC raid OR a human raider; the
  // PvP red wins the glow when there's no NPC line (so the colour reads "player").
  const alertColor = data.incomingAttack
    ? attackColor(data.incomingAttack.faction)
    : incomingPvp.length > 0
      ? '#EF4444'
      : null
  const showNames = shouldShowName(k)

  return (
    <div className="relative w-full h-full">
      {/* L'overlay « attaque entrante » est dessiné AVANT les routes pour que ces
          dernières (sœur DOM postérieure) gagnent le pointeur là où une route en
          vol croise une ligne d'attaque (près du repaire) : on préserve le survol/
          clic des routes, la ligne d'attaque reste cliquable partout ailleurs. */}
      {myIsland && (data.incomingAttack || incomingPvp.length > 0) && (
        <svg
          viewBox={`0 0 ${data.gridSize} ${data.gridSize}`}
          preserveAspectRatio="none"
          className="absolute inset-0 w-full h-full"
          // Le wrapper laisse passer le pan ; seules les bandes de clic des tracés
          // (pointerEvents:'stroke') captent le clic — comme RouteLayer.
          style={{ pointerEvents: 'none' }}
        >
          {data.incomingAttack && (
            <IncomingRaidLayer
              attack={data.incomingAttack}
              myIsland={myIsland}
              k={k}
              onOpen={() => onSelect(myIsland)}
            />
          )}
          {incomingPvp.length > 0 && (
            <IncomingPvpRaidLayer raids={incomingPvp} myIsland={myIsland} k={k} onOpen={() => onSelect(myIsland)} />
          )}
        </svg>
      )}

      {myIsland && tracesVisible && (
        <MapRoutes
          islands={data.islands}
          gridSize={data.gridSize}
          myIsland={myIsland}
          missions={inProgress}
          pvpRaids={pvpRaids?.sent ?? []}
          recits={recitsEnRoute}
          k={k}
          selectedIslandId={selected?.id ?? null}
          hoveredKey={hoveredKey}
          onHover={setHoveredKey}
          onSelectFleet={onSelectFleet}
        />
      )}

      {/* La bête vit à côté des îles, pas parmi elles : champ racine du payload,
          marqueur propre, aucun brouillard ni filtre de légende à traverser. */}
      {/* La carrière vit à côté des îles elle aussi, et sans clic : tout se joue sur
          l'écran du Grand Chantier, il n'y a pas de panneau à ouvrir ici. */}
      {data.quarrySite && (
        <QuarryMarker site={data.quarrySite} gridSize={data.gridSize} k={k} />
      )}
      {data.huntTarget && onSelectHunt && (
        <KrakenMarker
          target={data.huntTarget}
          gridSize={data.gridSize}
          k={k}
          active={huntSelected}
          onOpen={onSelectHunt}
        />
      )}

      {data.islands.map((i) => {
        const isUnknown = !i.isDiscovered
        const meta = islandMarkerMeta(i)
        const isActive = selected?.id === i.id
        const size = i.isMine ? 46 : 36
        const { Icon } = meta
        const underAttack = i.isMine && alertColor !== null
        const hit = Math.max(size, 44)
        // Légende cliquable : une île dont le type n'est pas sélectionné est
        // atténuée + non-interactive. Mon île ET l'île sélectionnée restent
        // toujours visibles (ancre + déselectionnable).
        const dimmed = filter ? !i.isMine && !isActive && !filter.isVisible(islandLegendKey(i)) : false
        return (
          <button
            key={i.id}
            id={`map-island-${i.id}`}
            type="button"
            onClick={() => onSelect(i)}
            title={i.name}
            disabled={dimmed}
            className={`absolute grid place-items-center bg-transparent ${i.isMine || isActive ? 'z-10' : ''}`}
            style={{
              left: `${(i.x / data.gridSize) * 100}%`,
              top: `${(i.y / data.gridSize) * 100}%`,
              width: `${hit}px`,
              height: `${hit}px`,
              transform: islandMarkerTransform(k, isActive),
              opacity: dimmed ? 0.25 : undefined,
              pointerEvents: dimmed ? 'none' : undefined,
            }}
          >
            {/* Cercle visuel (taille inchangée 36/46px), dans la zone de tap ≥44px. */}
            <span
              className={`relative grid place-items-center rounded-full ${underAttack ? 'animate-pulse' : ''}`}
              style={{
                width: `${size}px`,
                height: `${size}px`,
                background: 'rgba(6,34,43,.72)',
                border: i.isMine ? '2px solid #E5CC8F' : `1.5px ${isUnknown ? 'dashed' : 'solid'} ${meta.color}`,
                opacity: isUnknown ? 0.7 : 1,
                boxShadow: underAttack
                  ? `0 0 0 2px ${alertColor}, 0 0 16px 4px ${alertColor}88`
                  : isActive
                    ? '0 0 0 2.5px #7FE0F0, 0 0 24px 7px rgba(95,189,208,.6)'
                    : i.isMine
                      ? '0 0 0 2px #E5CC8F, 0 0 14px 3px rgba(229,204,143,.55)'
                      : 'none',
              }}
            >
              {isActive && !underAttack && (
                <span
                  aria-hidden
                  className="map-halo-pulse absolute rounded-full pointer-events-none"
                  style={{ inset: '-6px', border: '2px solid #7FE0F0' }}
                />
              )}
              <Icon className={`w-[58%] h-[58%] ${i.isMine ? 'text-sand-200' : ''}`} />
              {i.npcDanger !== null && (
                <span
                  className={`absolute -top-1.5 -right-1.5 text-[10px] font-bold rounded-full w-4 h-4 grid place-items-center border ${dangerTier(i.npcDanger).badge}`}
                >
                  {i.npcDanger}
                </span>
              )}
              {/* Mysterious islands carry no danger but a fixed difficulty palier —
                  a tier-coloured 1-4 badge to read the difficulty at a glance. */}
              {(() => {
                const tier = expeditionTierMeta(i.expeditionTier)

                return tier ? (
                  <span
                    className="absolute -top-1.5 -right-1.5 text-[9px] font-bold rounded-full w-4 h-4 grid place-items-center border"
                    style={{ background: tier.color, color: '#0b1b2b', borderColor: '#00000040' }}
                    title={`Difficulté : ${tier.label}`}
                    aria-label={`Difficulté : ${tier.label}`}
                  >
                    {tier.level}
                  </span>
                ) : null
              })()}
              <IslandFactionBadge island={i} />
            </span>
            {/* Nom : « Mon île » toujours visible, les autres au-delà du seuil de zoom. */}
            {(i.isMine || (showNames && i.isDiscovered)) && (
              <span className="absolute top-full mt-1 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-ocean-900/90 border border-sand-300/45 px-1.5 py-0.5 text-[10px] font-semibold text-sand-200 pointer-events-none">
                {i.isMine ? 'Mon île' : i.name}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}
